import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const HowItWorks = () => {
  const steps = [
    {
      number: "1",
      title: "Complete the Application",
      description:
        "Fill out our simple online application in just minutes. We only ask for basic business information — no lengthy paperwork required.",
      details: [
        "Takes less than 10 minutes",
        "No hard credit pull to apply",
        "Secure encrypted submission",
      ],
    },
    {
      number: "2",
      title: "Get Your Decision",
      description:
        "Our team reviews your application quickly and presents you with the best funding options tailored to your business needs.",
      details: [
        "Decision within hours, not weeks",
        "Multiple options to choose from",
        "Transparent terms and rates",
      ],
    },
    {
      number: "3",
      title: "Receive Your Funding",
      description:
        "Accept your offer, sign digitally, and receive funds directly in your business bank account — often the same day.",
      details: [
        "Same-day funding available",
        "Direct deposit to your account",
        "Start using funds immediately",
      ],
    },
  ];

  return (
    <>
      <SEO
        title="How It Works"
        description="Apply in minutes, get approved in hours, and receive funding the same day. See how Lloyd Funding makes business financing simple."
        canonical="/how-it-works"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              How It <span className="text-blue-700">Works</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Getting funded shouldn't be complicated. Our simple 3-step process
              gets you from application to funding in as little as 24 hours.
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#F8FAFC"
            />
          </svg>
        </div>
      </section>

      {/* Steps Section */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="space-y-16">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center lg:gap-16"
              >
                <div
                  className={`rounded-2xl bg-white p-6 shadow-lg ring-1 ring-slate-100 lg:bg-transparent lg:p-0 lg:shadow-none lg:ring-0 ${
                    index % 2 === 1 ? "lg:order-2" : ""
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white font-bold text-2xl">
                      {step.number}
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 sm:text-3xl">
                      {step.title}
                    </h2>
                  </div>
                  <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                    {step.description}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {step.details.map((detail) => (
                      <li key={detail} className="flex items-center gap-3">
                        <svg
                          className="h-5 w-5 text-emerald-500 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-slate-700">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div
                  className={`hidden lg:block relative ${
                    index % 2 === 1 ? "lg:order-1" : ""
                  }`}
                >
                  <div className="absolute -inset-4 rounded-[28px] bg-blue-200/20 blur-2xl" />
                  <div className="relative rounded-2xl bg-white p-8 shadow-xl ring-1 ring-slate-100">
                    <div className="flex items-center justify-center">
                      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                        {step.number === "1" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-12"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                            />
                          </svg>
                        )}
                        {step.number === "2" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-12"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                            />
                          </svg>
                        )}
                        {step.number === "3" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="size-12"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                    <p className="mt-6 text-center text-lg font-semibold text-slate-800">
                      Step {step.number}
                    </p>
                    <p className="mt-1 text-center text-slate-600">
                      {step.title}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
              What You'll Need
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Make sure you have these basics ready before applying.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { value: "6+ Months", label: "in business" },
              { value: "Business", label: "bank account" },
              { value: "$10K+", label: "monthly revenue" },
              { value: "500+", label: "credit score" },
            ].map((req) => (
              <div
                key={req.label}
                className="rounded-2xl bg-slate-50 px-6 py-8 text-center ring-1 ring-slate-100"
              >
                <p className="text-2xl font-bold text-blue-700">{req.value}</p>
                <p className="mt-1 text-sm text-slate-600">{req.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-800">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Ready to get started?
            </h2>
            <p className="mt-4 text-lg text-blue-100">
              Apply now and get funded as soon as today.
            </p>
            <div className="mt-8">
              <Link
                to="/contact#application-form"
                className="inline-flex items-center justify-center rounded-xl bg-white px-8 py-4 sm:py-3 text-base sm:text-sm font-semibold text-blue-800 shadow-sm hover:bg-blue-50 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
              >
                Start Your Application
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
              Process Questions
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Common questions about our application process
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "How long does the application take?",
                answer:
                  "The application takes just 5 minutes to complete. You'll need basic business information and we may ask for recent bank statements.",
              },
              {
                question: "When will I hear back about my application?",
                answer:
                  "Most applicants receive a decision within a few hours. In some cases, we may need additional information which could extend the timeline slightly.",
              },
              {
                question: "How quickly can I receive funds?",
                answer:
                  "Once approved, funds can be deposited as soon as the same business day. Most clients receive funding within 24-48 hours.",
              },
              {
                question: "What if I'm not approved?",
                answer:
                  "If you're not approved for one product, we'll work with you to explore alternative options. Our goal is to find a solution that works for your business.",
              },
            ].map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-100 bg-slate-50 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 p-5 text-slate-800">
                  <h3 className="text-lg font-semibold">{faq.question}</h3>
                  <svg
                    className="size-5 shrink-0 text-blue-600 transition-transform duration-300 group-open:-rotate-180"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <p className="px-5 pb-5 text-slate-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HowItWorks;
